import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { createPageUrl } from "@corporate/utils";
import { 
    Building2, 
    LayoutDashboard, 
    Receipt, 
    FileText, 
    TrendingUp, 
    Settings,
    ChevronDown,
    Globe,
    PieChart,
    CreditCard,
    Users,
    Menu,
    X,
    Package,
    Warehouse,
    FileCheck
} from "lucide-react";
import { Button } from "@corporate/components/ui/button";
import { Avatar, AvatarFallback } from "@corporate/components/ui/avatar";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@corporate/components/ui/dropdown-menu";
import { Company } from "@corporate/entities/Company";
import { User } from "@corporate/entities/User";

const navigationItems = [
    {
        title: "Dashboard",
        url: createPageUrl("Dashboard"),
        icon: LayoutDashboard,
    },
    {
        title: "Companies",
        url: createPageUrl("Companies"),
        icon: Building2,
    },
    {
        title: "Chart of Accounts",
        url: createPageUrl("Accounts"),
        icon: PieChart,
    },
    {
        title: "Inventory",
        url: createPageUrl("Inventory"),
        icon: Package,
    },
    {
        title: "Warehouses",
        url: createPageUrl("Warehouses"),
        icon: Warehouse,
    },
    {
        title: "Sales Invoices",
        url: createPageUrl("SalesInvoices"),
        icon: FileCheck,
    },
    {
        title: "Purchase Invoices",
        url: createPageUrl("PurchaseInvoices"),
        icon: Receipt,
    },
    {
        title: "Transactions",
        url: createPageUrl("Transactions"),
        icon: Receipt,
    },
    {
        title: "Ledger",
        url: createPageUrl("Ledger"),
        icon: FileText,
    },
    {
        title: "Reports",
        url: createPageUrl("Reports"),
        icon: FileText,
    },
    {
        title: "Settings",
        url: createPageUrl("Settings"),
        icon: Settings,
    },
];

export default function Layout({ children, currentPageName }) {
    const location = useLocation();
    const [companies, setCompanies] = useState([]);
    const [selectedCompany, setSelectedCompany] = useState(null);
    const [user, setUser] = useState(null);
    const [sidebarOpen, setSidebarOpen] = useState(false);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            const currentUser = await User.me();
            setUser(currentUser);
            
            const companiesData = await Company.list();
            setCompanies(companiesData);
            
            // Set first company as selected if none selected
            if (companiesData.length > 0 && !selectedCompany) {
                setSelectedCompany(companiesData[0]);
            }
        } catch (error) {
            console.error("Error loading data:", error);
        }
    };

    const handleCompanySelect = (company) => {
        setSelectedCompany(company);
        localStorage.setItem('selectedCompanyId', company.id);
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 no-print">
            <style>{`
                :root {
                    --primary-navy: #1e293b;
                    --primary-emerald: #10b981;
                    --accent-gold: #f59e0b;
                    --glass-bg: rgba(255, 255, 255, 0.85);
                    --glass-border: rgba(255, 255, 255, 0.2);
                }
                .glass-card {
                    background: var(--glass-bg);
                    backdrop-filter: blur(20px);
                    border: 1px solid var(--glass-border);
                    box-shadow: 0 8px 32px 0 rgba(31, 38, 135, 0.37);
                }
                .premium-gradient {
                    background: linear-gradient(135deg, var(--primary-navy) 0%, #334155 50%, var(--primary-emerald) 100%);
                }
                .text-gradient {
                    background: linear-gradient(135deg, var(--primary-navy), var(--primary-emerald));
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                    background-clip: text;
                }
                @media print {
                  .no-print {
                    display: none !important;
                  }
                  .printable-area {
                    display: block !important;
                  }
                  body {
                    background-color: #fff !important;
                  }
                }
            `}</style>

            {/* Mobile Header */}
            <div className="lg:hidden bg-white/90 backdrop-blur-lg border-b border-white/20 px-4 py-3 sticky top-0 z-50">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setSidebarOpen(!sidebarOpen)}
                            className="lg:hidden"
                        >
                            {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                        </Button>
                        <div className="flex items-center gap-2">
                            <div className="w-8 h-8 premium-gradient rounded-lg flex items-center justify-center">
                                <Globe className="w-4 h-4 text-white" />
                            </div>
                            <span className="font-semibold text-gradient">AccountingPro</span>
                        </div>
                    </div>
                    {selectedCompany && (
                        <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 border border-emerald-200">
                            <Building2 className="w-4 h-4 text-emerald-600" />
                            <span className="text-sm font-medium text-emerald-900">{selectedCompany.name}</span>
                        </div>
                    )}
                </div>
            </div>

            <div className="flex">
                {/* Sidebar */}
                <div className={`${
                    sidebarOpen ? 'translate-x-0' : '-translate-x-full'
                } lg:translate-x-0 fixed lg:static inset-y-0 left-0 w-72 glass-card border-r border-white/20 transition-transform duration-300 ease-in-out z-40 flex flex-col`}>
                    
                    {/* Logo */}
                    <div className="p-6 border-b border-white/10">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 premium-gradient rounded-xl flex items-center justify-center">
                                <Globe className="w-6 h-6 text-white" />
                            </div>
                            <div>
                                <h2 className="text-xl font-bold text-gradient">AccountingPro</h2>
                                <p className="text-xs text-slate-500">Professional Accounting Suite</p>
                            </div>
                        </div>
                    </div>

                    {/* Company Selector */}
                    {companies.length > 0 && (
                        <div className="p-4 border-b border-white/10">
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button variant="outline" className="w-full justify-between glass-card border-white/20 hover:bg-white/40">
                                        <div className="flex items-center gap-2">
                                            <Building2 className="w-4 h-4 text-emerald-600" />
                                            <span className="text-sm font-medium truncate">
                                                {selectedCompany?.name || "Select Company"}
                                            </span>
                                        </div>
                                        <ChevronDown className="w-4 h-4" />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent className="w-64 glass-card border-white/20">
                                    <DropdownMenuLabel>Switch Company</DropdownMenuLabel>
                                    <DropdownMenuSeparator className="bg-white/20" />
                                    {companies.map((company) => (
                                        <DropdownMenuItem
                                            key={company.id}
                                            onClick={() => handleCompanySelect(company)}
                                            className={`hover:bg-white/20 ${
                                                selectedCompany?.id === company.id ? 'bg-emerald-50' : ''
                                            }`}
                                        >
                                            <div className="flex flex-col">
                                                <span className="font-medium">{company.name}</span>
                                                <span className="text-xs text-slate-500">{company.currency}</span>
                                            </div>
                                        </DropdownMenuItem>
                                    ))}
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </div>
                    )}

                    {/* Navigation */}
                    <nav className="flex-1 p-4 space-y-2">
                        {navigationItems.map((item) => (
                            <Link
                                key={item.title}
                                to={item.url}
                                onClick={() => setSidebarOpen(false)}
                                className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${
                                    location.pathname === item.url
                                        ? 'bg-gradient-to-r from-emerald-500 to-emerald-600 text-white shadow-lg shadow-emerald-500/25'
                                        : 'hover:bg-white/30 text-slate-700'
                                }`}
                            >
                                <item.icon className="w-5 h-5" />
                                <span className="font-medium">{item.title}</span>
                            </Link>
                        ))}
                    </nav>

                    {/* User Profile */}
                    <div className="p-4 border-t border-white/10">
                        <Link
                            to="/"
                            className="flex items-center gap-3 px-4 py-2 mb-2 rounded-xl text-slate-600 hover:bg-white/30 transition-all"
                        >
                            <Globe className="w-4 h-4" />
                            <span className="text-sm font-medium">Back to CITIDAN</span>
                        </Link>
                        <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-white/20">
                            <Avatar className="w-8 h-8">
                                <AvatarFallback className="bg-emerald-500 text-white text-sm">
                                    {user?.full_name?.charAt(0) || 'U'}
                                </AvatarFallback>
                            </Avatar>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-slate-800 truncate">
                                    {user?.full_name || 'User'}
                                </p>
                                <p className="text-xs text-slate-500 truncate">{user?.email}</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Overlay */}
                {sidebarOpen && (
                    <div
                        className="fixed inset-0 bg-black/20 lg:hidden z-30"
                        onClick={() => setSidebarOpen(false)}
                    />
                )}

                {/* Main Content */}
                <div className="flex-1 min-w-0">
                    <main className="h-screen overflow-auto">
                        {children}
                    </main>
                </div>
            </div>
        </div>
    );
}